import { use } from "react";
import { useNavigate } from "react-router";
import Button from "../../components/Button";
import Form from "../../components/Form";
import FormField, { FieldInfo } from "../../components/FormField";
import Header from "../../components/Header";
import Input from "../../components/Input";
import Panel from "../../components/Panel";
import Stepper from "../../components/Stepper";
import { BikeContext } from "../../context/bikeContext";
import { usePreferences } from "../../hooks/useAccount";
import { useSetupMutation } from "../../hooks/useSetups";
import {
  CountingFrom,
  PostForkSettingsBody,
  PostShockSettingsBody,
  PostWheelsSettingsBody,
} from "../../http/onlyclicksApi";
import { translate } from "../../i18n";
import { Language } from "../../types";
import styles from "./NewSetupPage.module.css";

interface FormData {
  name: string;
  settings: {
    counting_from: CountingFrom;
    fork: PostForkSettingsBody;
    shock: PostShockSettingsBody;
    wheels: PostWheelsSettingsBody;
  };
}

const NewSetupPage = () => {
  const { bike } = use(BikeContext);
  const mutation = useSetupMutation();
  const navigate = useNavigate();
  const { data: preferences } = usePreferences();
  const i18n = translate(Language.English);

  if (!preferences || !bike) return null;

  const clicksMinValue = () => {
    switch (preferences.counting_from) {
      case "fully_closed":
        return -40;
      case "fully_open":
        return 0;
    }
  };

  const clicksMaxValue = () => {
    switch (preferences.counting_from) {
      case "fully_closed":
        return 0;
      case "fully_open":
        return 40;
    }
  };

  const validateClicks = (value: number | undefined) => {
    if (value && (value < clicksMinValue() || value > clicksMaxValue())) {
      return `value must be between ${clicksMinValue()} and ${clicksMaxValue()}`;
    }
    return undefined;
  };

  return (
    <div className={styles.layout}>
      <Header backLink="/setups" heading="Add a setup" />
      <Form<FormData>
        className={styles.form}
        defaultValues={{
          name: "",
          settings: {
            counting_from: preferences.counting_from,
            fork: {},
            shock: {},
            wheels: {
              tire_pressure_unit: preferences.tire_pressure_unit,
            },
          },
        }}
        onSubmit={({ value, formApi }) => {
          return mutation.mutateAsync(
            {
              name: value.name,
              bike_id: bike!.id,
              settings: value.settings,
            },
            {
              onSuccess: (setup) => {
                formApi.reset();
                navigate(`/setups/${setup.id}`);
              },
            },
          );
        }}
      >
        {(form) => (
          <>
            <div className={styles.formContent}>
              <Panel title="General">
                <form.Field
                  name="name"
                  validators={{
                    onChange: ({ value }) =>
                      !value ? "A name is required" : undefined,
                  }}
                >
                  {(field) => (
                    <FormField field={field}>
                      <Input
                        id={field.name}
                        name={field.name}
                        value={field.state.value}
                        placeholder="Name"
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                    </FormField>
                  )}
                </form.Field>
              </Panel>
              <Panel title="Fork">
                {bike.fork ? (
                  <>
                    {bike.fork.show_sag && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.fork.sag"
                          validators={{
                            onBlur: ({ value }) => {
                              if (value && (value < 0 || value > 100)) {
                                return "value must be between 0 and 100";
                              }
                              return undefined;
                            },
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>{i18n.sag}</label>
                              <Stepper
                                className={styles.control}
                                unit="%"
                                minValue={0}
                                maxValue={100}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                    {bike.fork.type == "air" && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.fork.psi"
                          validators={{
                            onBlur: ({ value }) => {
                              if (value && (value < 0 || value > 350)) {
                                return "value must be between 0 and 350";
                              }
                              return undefined;
                            },
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>
                                {i18n.air_pressure}
                              </label>
                              <Stepper
                                className={styles.control}
                                unit={i18n.psi}
                                minValue={0}
                                maxValue={350}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                    {bike.fork.type == "coil" && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.fork.lbs"
                          validators={{
                            onBlur: ({ value }) => {
                              if (value && (value < 200 || value > 800)) {
                                return "value must be between 200 and 800";
                              }
                              return undefined;
                            },
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>
                                {i18n.spring_weight}
                              </label>
                              <Stepper
                                className={styles.control}
                                unit={i18n.lbs}
                                minValue={200}
                                maxValue={800}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                    {bike.fork.show_lsc && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.fork.lsc"
                          validators={{
                            onBlur: ({ value }) => validateClicks(value),
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>
                                {i18n.lsc(false)}
                              </label>
                              <Stepper
                                className={styles.control}
                                unit={i18n.clicks}
                                minValue={clicksMinValue()}
                                maxValue={clicksMaxValue()}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                    {bike.fork.show_hsc && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.fork.hsc"
                          validators={{
                            onBlur: ({ value }) => validateClicks(value),
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>
                                {i18n.hsc(false)}
                              </label>
                              <Stepper
                                className={styles.control}
                                unit={i18n.clicks}
                                minValue={clicksMinValue()}
                                maxValue={clicksMaxValue()}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                    {bike.fork.show_lsr && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.fork.lsr"
                          validators={{
                            onBlur: ({ value }) => validateClicks(value),
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>
                                {i18n.lsr(bike.fork!.show_hsr, false)}
                              </label>
                              <Stepper
                                className={styles.control}
                                unit={i18n.clicks}
                                minValue={clicksMinValue()}
                                maxValue={clicksMaxValue()}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                    {bike.fork.show_hsr && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.fork.hsr"
                          validators={{
                            onBlur: ({ value }) => validateClicks(value),
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>
                                {i18n.hsr(false)}
                              </label>
                              <Stepper
                                className={styles.control}
                                unit={i18n.clicks}
                                minValue={clicksMinValue()}
                                maxValue={clicksMaxValue()}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                    {bike.fork.show_volume_spacers && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.fork.volume_spacers"
                          validators={{
                            onBlur: ({ value }) => {
                              if (value && (value < 0 || value > 10)) {
                                return "value must be between 0 and 10";
                              }
                              return undefined;
                            },
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>
                                {i18n.volume_spacers}
                              </label>
                              <Stepper
                                className={styles.control}
                                unit={i18n.quantity}
                                minValue={0}
                                maxValue={10}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                    {bike.fork.show_negative_psi && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.fork.negative_psi"
                          validators={{
                            onBlur: ({ value }) => {
                              if (value && (value < 0 || value > 350)) {
                                return "value must be between 0 and 350";
                              }
                              return undefined;
                            },
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>
                                {i18n.negative_air_pressure(false)}
                              </label>
                              <Stepper
                                className={styles.control}
                                unit={i18n.psi}
                                minValue={0}
                                maxValue={350}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <span>Fork not configured.</span>
                )}
              </Panel>
              <Panel title="Shock">
                {bike.shock ? (
                  <>
                    {bike.shock.show_sag && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.shock.sag"
                          validators={{
                            onBlur: ({ value }) => {
                              if (value && (value < 0 || value > 100)) {
                                return "value must be between 0 and 100";
                              }
                              return undefined;
                            },
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>{i18n.sag}</label>
                              <Stepper
                                className={styles.control}
                                unit="%"
                                minValue={0}
                                maxValue={100}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                    {bike.shock.type == "air" && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.shock.psi"
                          validators={{
                            onBlur: ({ value }) => {
                              if (value && (value < 0 || value > 350)) {
                                return "value must be between 0 and 350";
                              }
                              return undefined;
                            },
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>
                                {i18n.air_pressure}
                              </label>
                              <Stepper
                                className={styles.control}
                                unit={i18n.psi}
                                minValue={0}
                                maxValue={350}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                    {bike.shock.type == "coil" && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.shock.lbs"
                          validators={{
                            onBlur: ({ value }) => {
                              if (value && (value < 200 || value > 800)) {
                                return "value must be between 200 and 800";
                              }
                              return undefined;
                            },
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>
                                {i18n.spring_weight}
                              </label>
                              <Stepper
                                className={styles.control}
                                unit={i18n.lbs}
                                minValue={200}
                                maxValue={800}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                    {bike.shock.show_lsc && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.shock.lsc"
                          validators={{
                            onBlur: ({ value }) => validateClicks(value),
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>
                                {i18n.lsc(false)}
                              </label>
                              <Stepper
                                className={styles.control}
                                unit={i18n.clicks}
                                minValue={clicksMinValue()}
                                maxValue={clicksMaxValue()}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                    {bike.shock.show_hsc && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.shock.hsc"
                          validators={{
                            onBlur: ({ value }) => validateClicks(value),
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>
                                {i18n.hsc(false)}
                              </label>
                              <Stepper
                                className={styles.control}
                                unit={i18n.clicks}
                                minValue={clicksMinValue()}
                                maxValue={clicksMaxValue()}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                    {bike.shock.show_lsr && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.shock.lsr"
                          validators={{
                            onBlur: ({ value }) => validateClicks(value),
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>
                                {i18n.lsr(bike.shock!.show_hsr, false)}
                              </label>
                              <Stepper
                                className={styles.control}
                                unit={i18n.clicks}
                                minValue={clicksMinValue()}
                                maxValue={clicksMaxValue()}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                    {bike.shock.show_hsr && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.shock.hsr"
                          validators={{
                            onBlur: ({ value }) => validateClicks(value),
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>
                                {i18n.hsr(false)}
                              </label>
                              <Stepper
                                className={styles.control}
                                unit={i18n.clicks}
                                minValue={clicksMinValue()}
                                maxValue={clicksMaxValue()}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                    {bike.shock.show_volume_spacers && (
                      <div className={styles.field}>
                        <form.Field
                          name="settings.shock.volume_spacers"
                          validators={{
                            onBlur: ({ value }) => {
                              if (value && (value < 0 || value > 10)) {
                                return "value must be between 0 and 10";
                              }
                              return undefined;
                            },
                          }}
                          children={(field) => (
                            <>
                              <label className={styles.label}>
                                {i18n.volume_spacers}
                              </label>
                              <Stepper
                                className={styles.control}
                                unit={i18n.quantity}
                                minValue={0}
                                maxValue={10}
                                value={field.state.value}
                                onChange={(e) => {
                                  field.handleChange(e);
                                  form.validateField(field.name, "blur");
                                }}
                                countingFrom={preferences.counting_from}
                              />
                              <FieldInfo field={field} align="right" />
                            </>
                          )}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <span>Shock not configured.</span>
                )}
              </Panel>
              <Panel title="Wheels">
                {bike.show_wheels ? (
                  <>
                    {preferences.tire_pressure_unit === "bar" && (
                      <>
                        <div className={styles.field}>
                          <form.Field
                            name="settings.wheels.front_bar"
                            children={(field) => (
                              <>
                                <label className={styles.label}>
                                  {i18n.front_pressure}
                                </label>
                                <Stepper
                                  className={styles.control}
                                  unit={i18n.tirePressureUnit(
                                    preferences.tire_pressure_unit,
                                  )}
                                  minValue={0}
                                  step={0.1}
                                  value={field.state.value}
                                  onChange={(e) => {
                                    field.handleChange(e);
                                    form.validateField(field.name, "blur");
                                  }}
                                  countingFrom={preferences.counting_from}
                                />
                                <FieldInfo field={field} align="right" />
                              </>
                            )}
                          />
                        </div>
                        <div className={styles.field}>
                          <form.Field
                            name="settings.wheels.rear_bar"
                            children={(field) => (
                              <>
                                <label className={styles.label}>
                                  {i18n.rear_pressure}
                                </label>
                                <Stepper
                                  className={styles.control}
                                  unit={i18n.tirePressureUnit(
                                    preferences.tire_pressure_unit,
                                  )}
                                  minValue={0}
                                  step={0.1}
                                  value={field.state.value}
                                  onChange={(e) => {
                                    field.handleChange(e);
                                    form.validateField(field.name, "blur");
                                  }}
                                  countingFrom={preferences.counting_from}
                                />
                                <FieldInfo field={field} align="right" />
                              </>
                            )}
                          />
                        </div>
                      </>
                    )}
                    {preferences.tire_pressure_unit === "psi" && (
                      <>
                        <div className={styles.field}>
                          <form.Field
                            name="settings.wheels.front_psi"
                            children={(field) => (
                              <>
                                <label className={styles.label}>
                                  Front Pressure
                                </label>
                                <Stepper
                                  className={styles.control}
                                  unit={i18n.tirePressureUnit(
                                    preferences.tire_pressure_unit,
                                  )}
                                  minValue={0}
                                  step={0.5}
                                  value={field.state.value}
                                  onChange={(e) => {
                                    field.handleChange(e);
                                    form.validateField(field.name, "blur");
                                  }}
                                  countingFrom={preferences.counting_from}
                                />
                                <FieldInfo field={field} align="right" />
                              </>
                            )}
                          />
                        </div>
                        <div className={styles.field}>
                          <form.Field
                            name="settings.wheels.rear_psi"
                            children={(field) => (
                              <>
                                <label className={styles.label}>
                                  Rear Pressure
                                </label>
                                <Stepper
                                  className={styles.control}
                                  unit={i18n.tirePressureUnit(
                                    preferences.tire_pressure_unit,
                                  )}
                                  minValue={0}
                                  step={0.5}
                                  value={field.state.value}
                                  onChange={(e) => {
                                    field.handleChange(e);
                                    form.validateField(field.name, "blur");
                                  }}
                                  countingFrom={preferences.counting_from}
                                />
                                <FieldInfo field={field} align="right" />
                              </>
                            )}
                          />
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <span>Wheels not configured.</span>
                )}
              </Panel>
            </div>
            <div className={styles.submitBar}>
              <Button
                type="accent"
                fullWidth={true}
                isLoading={mutation.isPending}
                buttonProps={{
                  type: "submit",
                  disabled: mutation.isPending,
                }}
              >
                Create
              </Button>
            </div>
          </>
        )}
      </Form>
    </div>
  );
};

export default NewSetupPage;
